import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Menu, MenuItem, Button, Tabs, Tab, alpha, useTheme, Snackbar, Alert } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import api from '../api';

const APP_NAME = 'FleetPulse';
const TAGLINE = 'Fleet Maintenance Made Easy';
const showApiDebug = process.env.REACT_APP_SHOW_API_DEBUG === 'true' && window.location.hostname === 'localhost';

export default function Header({ children, onLogout, showUserMenu = false, onProfile, onLogoClick, onLanding, planSummary, currentView, onNavigate, userInfo, subscriptionData, onMenuClick, onUpgrade, canSeeVehicles = true, onResetChoice, upgradeModalOpen = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const theme = useTheme();

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleManageSubscription = async () => {
    try {
      const response = await api.post('/subscription/portal/');
      localStorage.setItem('forceSubscriptionSync', 'true');
      window.location.href = response.data.portal_url;
    } catch (error) {
      if (error.response?.data?.manual_upgrade) {
        setSnackbar({ 
          open: true, 
          message: '⚠️ MANUAL UPGRADE ACCOUNT: Your subscription was activated by an administrator and cannot be modified through Stripe. Please email support@myfleetpulse.app or contact us in-app to make changes to your plan.', 
          severity: 'warning' 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: 'Unable to open subscription portal. Please try again.', 
          severity: 'error' 
        });
      }
    }
    handleClose();
  };

  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{
        background: alpha(theme.palette.background.default, 0.85),
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 3 } }}>
        {/* Hamburger menu for mobile */}
        {onMenuClick && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMenuClick}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              '&:hover': {
                background: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            mr: 3, 
            cursor: onLogoClick ? 'pointer' : 'default',
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.9 },
          }}
          onClick={onLogoClick || undefined}
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo.jpg`}
            alt="FleetPulse Logo"
            style={{
              width: 36,
              height: 36,
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          />
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              lineHeight: 1.1, 
              fontSize: '1.1rem',
              background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              {APP_NAME}
            </Typography>
            {planSummary && (
              <Typography variant="caption" sx={{ 
                display: 'block', 
                color: alpha(theme.palette.text.primary, 0.6),
                fontSize: '0.7rem',
                letterSpacing: '0.02em',
              }}>
                {planSummary}
              </Typography>
            )}
          </Box>
        </Box>

        {showApiDebug && (
          <Typography variant="caption" sx={{ ml: 2, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'text.secondary' }}>
            API: {process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}
          </Typography>
        )}
        
        {/* Navigation Tabs - only show when logged in */}
        {onNavigate && (
          <Tabs 
            value={['dashboard', 'vehicles', 'maintenance', 'reminders'].includes(currentView) ? `/${currentView}` : false}
            onChange={(e, newValue) => onNavigate(newValue)}
            sx={{ 
              ml: 2,
              display: { xs: 'none', lg: 'block' },
              '& .MuiTab-root': { 
                minHeight: 48,
                color: alpha(theme.palette.text.primary, 0.6),
                fontWeight: 500,
                fontSize: '0.9rem',
                transition: 'all 0.2s ease',
                '&:hover': {
                  color: theme.palette.text.primary,
                  background: alpha(theme.palette.primary.main, 0.08),
                },
                '&.Mui-selected': { 
                  color: theme.palette.primary.light,
                },
              },
              '& .MuiTabs-indicator': { 
                background: theme.palette.primary.main,
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Dashboard" value="/dashboard" />
            {canSeeVehicles && <Tab label="Vehicles" value="/vehicles" />}
            <Tab label="Service History" value="/maintenance" />
            <Tab label="Reminders" value="/reminders" />
          </Tabs>
        )}
        
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Upgrade Button for Free/Pro Users */}
        {showUserMenu && subscriptionData && (subscriptionData.plan === 'free' || subscriptionData.plan === 'pro') && onUpgrade && !upgradeModalOpen && (
          <Button
            variant="contained"
            startIcon={<RocketLaunchIcon sx={{ fontSize: 18 }} />}
            onClick={onUpgrade}
            size="small"
            sx={{
              mr: 2,
              background: theme.palette.primary.main,
              color: 'white',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 1.5,
              py: 0.5,
              display: { xs: 'none', md: 'flex' },
              boxShadow: 'none',
              '&:hover': {
                background: theme.palette.primary.dark,
                transform: 'none',
                boxShadow: 'none',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {subscriptionData.plan === 'pro' ? 'Upgrade to Fleet' : 'Upgrade to Pro'}
          </Button>
        )}
        
        {showUserMenu && (
          <>
            {userInfo && (
              <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' }, color: 'text.secondary' }}>
                {userInfo.username}
              </Typography>
            )}
            <IconButton
              size="large"
              aria-label="account menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              sx={{
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <AccountCircleIcon sx={{ color: 'text.secondary' }} />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 180,
                }
              }}
            >
              <MenuItem onClick={() => { handleClose(); onProfile && onProfile(); }}>
                My Profile
              </MenuItem>
              {subscriptionData && subscriptionData.plan && subscriptionData.plan !== 'free' && (
                <MenuItem onClick={handleManageSubscription}>
                  Manage Subscription
                </MenuItem>
              )}
              <MenuItem onClick={() => { handleClose(); onLogout && onLogout(); }}>
                Logout
              </MenuItem>
            </Menu>
          </>
        )}
        
        {/* Logout Button */}
        {showUserMenu && onLogout && (
          <Button
            variant="outlined"
            startIcon={<LogoutIcon />}
            onClick={onLogout}
            sx={{ 
              ml: 1,
              display: { xs: 'none', sm: 'flex' },
              borderColor: 'rgba(255, 255, 255, 0.15)',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
                background: 'rgba(255, 255, 255, 0.05)',
              }
            }}
          >
            Logout
          </Button>
        )}
        {children}
      </Toolbar>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'warning' ? 12000 : 6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          top: { xs: 70, md: 80 },
          '& .MuiSnackbar-root': {
            maxWidth: '95vw'
          }
        }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            maxWidth: 800,
            fontSize: '0.95rem',
            fontWeight: 600,
            '& .MuiAlert-message': {
              width: '100%',
              py: 0.5
            },
            ...(snackbar.severity === 'warning' && {
              bgcolor: '#f59e0b',
              color: '#000',
              '& .MuiAlert-icon': {
                color: '#000'
              }
            })
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppBar>
  );
}

export { APP_NAME, TAGLINE };
